import {
  useAddProductToCartMutation,
  useDeleteCartProductMutation,
  useGetCartProductsQuery,
  useUpdateProductInCartMutation,
} from 'graphql/generated/remote-schema-hasura';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import toast from 'react-hot-toast';
import { noop } from 'lodash';
import { CartProductOutput, ProductOutput } from 'graphql/generated/hasura';
import { groupProductsByPatientMap } from '../app/shop/components/Tests/CartModal';
import { useAuth } from '../auth/context/AuthContext';

export interface CartProduct {
  id: number;
  product_id: number;
  quantity: number;
  product: ProductOutput | null;
}

export const MAX_QUANTITY = 5;
export const MIN_QUANTITY = 0;
const DEBOUNCE_TIME = 1000;

const useShopCart = (finallyCallback: () => void = noop) => {
  const [isLoadingCartData, updateIsLoadingCartData] = useState(false);
  const [isInitialLoading, updateIsInitialLoading] = useState(false);
  const [isLoadingRequests, updateIsLoadingRequests] = useState(false);

  const { selectedAccount, user } = useAuth();

  const [cartProducts, updateCartProducts] = useState<Array<CartProductOutput>>(
    [],
  );
  const [selectedProducts, updateSelectedProducts] = useState<
    Record<string, number>
  >({});

  const removeCartProductByProductId = (productId: number | null) => {
    if (!productId) return;
    const currentCartProduct = cartProducts.find(
      ({ product_id }) => product_id === Number(productId),
    );

    if (!currentCartProduct || !currentCartProduct?.id) return;
    updateIsLoadingCartData(true);
    return updateSelectedProducts((prev) => ({
      ...prev,
      [currentCartProduct.id]: 0,
    }));
  };

  const [debouncedSelectedProducts] = useDebounce(
    selectedProducts,
    DEBOUNCE_TIME,
  );

  const { refetch, data } = useGetCartProductsQuery({
    onCompleted: ({ getCartProducts }) => {
      const products = getCartProducts.cart_products ?? [];
      updateCartProducts(products as CartProductOutput[]);
    },
  });

  const [addProduct] = useAddProductToCartMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['GetCartProducts'],
  });
  const [updateProduct] = useUpdateProductInCartMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['GetCartProducts'],
  });
  const [deleteProduct] = useDeleteCartProductMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['GetCartProducts'],
  });

  const totalPrice = useMemo(() => {
    return cartProducts.reduce(
      (prev, next) => prev + next.quantity * (next?.product?.price ?? 0),
      0,
    );
  }, [cartProducts]);

  const totalItems = useMemo(() => {
    return cartProducts.reduce((prev, next) => prev + next.quantity, 0);
  }, [cartProducts]);

  const getCartProducts = useCallback(
    (enableInitialLoader = false, revalidate = false) => {
      if (enableInitialLoader) {
        updateIsInitialLoading(true);
      }
      updateIsLoadingCartData(true);
      return refetch()
        .then(({ data }) => {
          const products = data?.getCartProducts.cart_products ?? [];
          if (enableInitialLoader || revalidate) {
            updateSelectedProducts(() => ({
              ...products.reduce(
                (prev, next) => ({ ...prev, [next.id]: next.quantity }),
                {},
              ),
            }));
          }
        })
        .finally(() => {
          updateIsInitialLoading(false);
          updateIsLoadingCartData(false);
        });
    },
    [refetch],
  );

  const addRecommendationToCart = useCallback(
    (productId: number, quantity: number, recommendationId: string | null) => {
      updateIsLoadingCartData(true);
      return addProduct({
        variables: {
          productId: Number(productId),
          quantity: quantity,
          recommendationId,
        },
      }).finally(() => updateIsLoadingCartData(false));
    },
    [addProduct],
  );

  const updateProducts = useCallback(
    (selectedProducts: Record<string, number>) => {
      updateIsLoadingRequests(true);
      return Promise.all(
        Object.keys(selectedProducts).map((productId) => {
          const currentCartProduct = cartProducts.find(
            ({ id }) => id === Number(productId),
          );
          const newQuantity = Math.min(
            Math.max(selectedProducts[productId], MIN_QUANTITY),
            MAX_QUANTITY,
          );

          if (!currentCartProduct) return Promise.resolve(false);

          if (currentCartProduct?.quantity === newQuantity)
            return Promise.resolve(false);

          if (
            (!newQuantity || (currentCartProduct?.id && !newQuantity)) &&
            currentCartProduct?.id
          ) {
            return deleteProduct({ variables: { id: currentCartProduct.id } });
          }

          if (newQuantity > 0) {
            return updateProduct({
              variables: {
                id: currentCartProduct?.id,
                quantity: Math.max(newQuantity, 1),
              },
            });
          }
        }),
      )
        .then((data) => {
          if (data.some((hasChanges) => hasChanges)) {
            return getCartProducts(false);
          }
        })
        .catch((error) => {
          toast.error(error.message);
          return getCartProducts(false, true);
        })
        .finally(() => {
          updateIsLoadingRequests(false);
          finallyCallback();
        });
    },
    [
      cartProducts,
      updateProduct,
      deleteProduct,
      getCartProducts,
      finallyCallback,
    ],
  );

  useEffect(() => {
    updateProducts(debouncedSelectedProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSelectedProducts]);

  useEffect(() => {
    getCartProducts(true);
  }, [getCartProducts]);

  const cards = useMemo(
    () => groupProductsByPatientMap(cartProducts),
    [cartProducts],
  );

  const currentSelectedProducts = useMemo(
    () =>
      cards
        .get(selectedAccount || user?.uuid || '')
        ?.products?.map(({ id }) => id),
    [cards, selectedAccount, user?.uuid],
  );

  return {
    data: data?.getCartProducts,
    totalPrice,
    totalItems,
    selectedProducts,
    currentSelectedProducts,
    isLoadingCartData,
    updateSelectedProducts,
    removeCartProductByProductId,
    cartProducts,
    isInitialLoading,
    isLoadingRequests,
    addRecommendationToCart,
  };
};

export default useShopCart;
